import { useEffect, useState } from "react";
import { Receipt, RefreshCw, Download, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { STATUS_BADGE, fmtDate } from "./Transactions";

const fmt = (n, cur) => (cur === "usd" ? "$" : "Rp") + Number(n || 0).toLocaleString("id-ID");

const FILTERS = [
  { id: "", label: "Semua" },
  { id: "paid", label: "Berhasil" },
  { id: "pending", label: "Menunggu" },
  { id: "failed", label: "Gagal" },
];

export default function AdminTransactions() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [onlyUncredited, setOnlyUncredited] = useState(false);
  const [crediting, setCrediting] = useState(null);

  const load = (f = filter, df = from, dt = to) => {
    setRows(null);
    const p = new URLSearchParams();
    if (f) p.set("status", f);
    if (df) p.set("date_from", df);
    if (dt) p.set("date_to", dt);
    const qs = p.toString();
    api.get(`/admin/transactions${qs ? "?" + qs : ""}`).then(({ data }) => setRows(data.transactions)).catch(() => setRows([]));
  };
  useEffect(() => { load("", "", ""); }, []);

  const credit = async (orderId) => {
    if (!window.confirm("Kreditkan manfaat transaksi ini ke akun siswa? Lakukan hanya bila pembayaran sudah benar-benar diterima.")) return;
    setCrediting(orderId);
    try {
      const { data } = await api.post(`/admin/transactions/${orderId}/regrant`);
      toast.success(data.message || "Berhasil dikreditkan.");
      load();
      if (detail?.txn?.order_id === orderId) openDetail(orderId);
    } catch (e) { toast.error(apiErr(e)); } finally { setCrediting(null); }
  };

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const openDetail = async (orderId) => {
    setDetail("loading"); setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/transactions/${orderId}/detail`);
      setDetail(data);
    } catch (e) { toast.error(apiErr(e)); setDetail(null); } finally { setDetailLoading(false); }
  };

  const preset = (kind) => {
    const now = new Date();
    let df = "", dt = "";
    if (kind === "month") {
      df = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
      dt = now.toISOString().slice(0, 10);
    } else if (kind === "30d") {
      df = new Date(now.getTime() - 29 * 86400000).toISOString().slice(0, 10);
      dt = now.toISOString().slice(0, 10);
    }
    setFrom(df); setTo(dt); load(filter, df, dt);
  };

  const totalPaid = (rows || []).filter((r) => r.status === "paid").reduce((s, r) => s + Number(r.amount || 0), 0);
  const uncreditedCount = (rows || []).filter((r) => r.status === "paid" && !r.granted).length;
  const visible = onlyUncredited ? (rows || []).filter((r) => r.status === "paid" && !r.granted) : rows;

  const exportCSV = () => {
    const data = rows || [];
    if (!data.length) return;
    const headers = ["Waktu", "Nama", "Email", "Produk", "Gateway", "Metode", "Nominal", "Mata Uang", "Status", "Order ID"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = data.map((t) => [
      fmtDate(t.created_at), t.user_name, t.user_email, t.product_name, t.gateway,
      t.method, t.amount, t.currency, (STATUS_BADGE[t.status] || {}).label || t.status, t.order_id,
    ].map(esc).join(","));
    const csv = [headers.map(esc).join(","), ...lines].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transaksi-skorup-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="admin-transactions">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Receipt className="w-5 h-5 text-su-primary" />
          <h2 className="font-heading font-black text-lg">Riwayat Transaksi</h2>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="txn-export-csv" onClick={exportCSV} disabled={!rows || !rows.length}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-su-border bg-su-surface font-heading font-extrabold text-sm hover:border-su-success disabled:opacity-50">
            <Download className="w-4 h-4" /> Unduh CSV
          </button>
          <button data-testid="txn-refresh" onClick={() => load()} className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-su-border bg-su-surface font-heading font-extrabold text-sm hover:border-su-primary">
            <RefreshCw className="w-4 h-4" /> Muat Ulang
          </button>
        </div>
      </div>

      <div className="card-chunky p-3 flex flex-wrap items-end gap-3" data-testid="txn-date-filter">
        <label className="flex flex-col gap-1 text-xs font-heading font-black text-su-muted">
          Dari
          <input data-testid="txn-date-from" type="date" value={from}
            onChange={(e) => { setFrom(e.target.value); load(filter, e.target.value, to); }}
            className="px-3 py-1.5 rounded-xl border-2 border-su-border bg-su-bg text-su-text font-medium text-sm outline-none focus:border-su-primary" />
        </label>
        <label className="flex flex-col gap-1 text-xs font-heading font-black text-su-muted">
          Sampai
          <input data-testid="txn-date-to" type="date" value={to}
            onChange={(e) => { setTo(e.target.value); load(filter, from, e.target.value); }}
            className="px-3 py-1.5 rounded-xl border-2 border-su-border bg-su-bg text-su-text font-medium text-sm outline-none focus:border-su-primary" />
        </label>
        <div className="flex gap-2">
          <button data-testid="txn-preset-month" onClick={() => preset("month")} className="px-3 py-1.5 rounded-xl border-2 border-su-border bg-su-surface font-heading font-extrabold text-sm hover:border-su-primary">Bulan Ini</button>
          <button data-testid="txn-preset-30d" onClick={() => preset("30d")} className="px-3 py-1.5 rounded-xl border-2 border-su-border bg-su-surface font-heading font-extrabold text-sm hover:border-su-primary">30 Hari</button>
          <button data-testid="txn-preset-all" onClick={() => { setFrom(""); setTo(""); load(filter, "", ""); }} className="px-3 py-1.5 rounded-xl border-2 border-su-border bg-su-surface font-heading font-extrabold text-sm hover:border-su-primary">Semua Waktu</button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        {FILTERS.map((f) => (
          <button key={f.id} data-testid={`txn-filter-${f.id || "all"}`} onClick={() => { setFilter(f.id); load(f.id); }}
            className={`px-3 py-1.5 rounded-xl font-heading font-extrabold text-sm border-2 transition-all ${filter === f.id ? "bg-su-primary text-white border-su-primaryShadow" : "bg-su-surface text-su-muted border-su-border"}`}>
            {f.label}
          </button>
        ))}
        <button data-testid="txn-filter-uncredited" onClick={() => setOnlyUncredited((v) => !v)}
          className={`px-3 py-1.5 rounded-xl font-heading font-extrabold text-sm border-2 transition-all inline-flex items-center gap-1.5 ${onlyUncredited ? "bg-su-danger text-white border-su-danger" : "bg-su-surface text-su-danger border-su-danger/40"}`}>
          <AlertTriangle className="w-4 h-4" /> Lunas tapi belum masuk
        </button>
        <div className="ml-auto px-3 py-1.5 rounded-xl bg-su-success/15 text-su-success font-heading font-black text-sm">
          Total berhasil: {fmt(totalPaid, "idr")}
        </div>
      </div>

      {uncreditedCount > 0 && (
        <div className="card-chunky p-4 border-su-danger/40 bg-su-danger/5 flex items-center gap-3" data-testid="txn-uncredited-warning">
          <AlertTriangle className="w-5 h-5 text-su-danger shrink-0" />
          <p className="text-sm font-medium"><b>{uncreditedCount} transaksi LUNAS tapi manfaatnya BELUM masuk</b> ke akun siswa. Tinjau & kreditkan manual bila pembayaran valid.</p>
        </div>
      )}

      {rows === null ? (
        <div className="min-h-[30vh] grid place-items-center"><div className="w-10 h-10 rounded-full border-4 border-su-border border-t-su-primary animate-spin" /></div>
      ) : visible.length === 0 ? (
        <div className="card-chunky p-8 text-center text-su-muted font-medium">{onlyUncredited ? "Tidak ada transaksi lunas yang belum masuk. Semua aman! ✅" : "Belum ada transaksi."}</div>
      ) : (
        <div className="card-chunky overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b-2 border-su-border">
                <th className="p-3 font-heading font-black text-su-muted uppercase text-xs">Waktu</th>
                <th className="p-3 font-heading font-black text-su-muted uppercase text-xs">Pengguna</th>
                <th className="p-3 font-heading font-black text-su-muted uppercase text-xs">Produk</th>
                <th className="p-3 font-heading font-black text-su-muted uppercase text-xs">Nominal</th>
                <th className="p-3 font-heading font-black text-su-muted uppercase text-xs">Status Bayar</th>
                <th className="p-3 font-heading font-black text-su-muted uppercase text-xs">Masuk ke Akun?</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((t) => {
                const b = STATUS_BADGE[t.status] || STATUS_BADGE.pending;
                const needsCredit = t.status === "paid" && !t.granted;
                return (
                  <tr key={t.order_id} onClick={() => openDetail(t.order_id)} className={`border-b border-su-border/50 cursor-pointer hover:bg-su-bg/60 ${needsCredit ? "bg-su-danger/5" : ""}`} data-testid={`admin-txn-${t.order_id}`}>
                    <td className="p-3 font-mono text-xs text-su-muted whitespace-nowrap">{fmtDate(t.created_at)}</td>
                    <td className="p-3"><div className="font-semibold">{t.user_name}</div><div className="text-xs text-su-muted">{t.user_email}</div></td>
                    <td className="p-3 font-semibold">{t.product_name}</td>
                    <td className="p-3 font-heading font-black whitespace-nowrap">{fmt(t.amount, t.currency)}</td>
                    <td className="p-3"><span className={`inline-block px-2 py-0.5 rounded-lg text-xs font-heading font-black ${b.cls}`}>{b.label}</span></td>
                    <td className="p-3">
                      {t.granted ? (
                        <span className="inline-flex items-center gap-1 text-su-success font-heading font-black text-xs" data-testid={`txn-granted-${t.order_id}`}><CheckCircle2 className="w-4 h-4" /> Sudah masuk{t.manual_grant ? " (manual)" : ""}</span>
                      ) : needsCredit ? (
                        <button data-testid={`txn-credit-${t.order_id}`} onClick={(e) => { e.stopPropagation(); credit(t.order_id); }} disabled={crediting === t.order_id}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-su-danger text-white font-heading font-black text-xs hover:opacity-90 disabled:opacity-50">
                          {crediting === t.order_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5" />} Belum · Kreditkan
                        </button>
                      ) : (
                        <span className="text-xs text-su-muted font-medium">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => setDetail(null)} data-testid="txn-detail-modal">
          <div className="card-chunky p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {detail === "loading" || detailLoading ? (
              <div className="py-10 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-su-primary" /></div>
            ) : (
              <TxnDetail d={detail} onCredit={() => credit(detail.txn.order_id)} crediting={crediting === detail.txn.order_id} onClose={() => setDetail(null)} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, before, after, changed }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-su-border/40 text-sm">
      <span className="text-su-muted font-medium">{label}</span>
      <span className="flex items-center gap-2 font-heading font-black">
        <span className={after != null && changed ? "text-su-muted line-through" : ""}>{before}</span>
        {after != null && changed && <><span className="text-su-muted">→</span><span className="text-su-success">{after}</span></>}
      </span>
    </div>
  );
}

function TxnDetail({ d, onCredit, crediting, onClose }) {
  const t = d.txn, u = d.user || {};
  const g = d.expected_grant || {};
  const slots = d.expected_tryout_slots || 0;
  const pending = t.status === "paid" && !t.granted; // lunas tapi belum masuk → tampilkan perkiraan sesudah
  const vipTxt = (n) => n ? `+${n} hari VIP` : null;
  const fmtVip = u.vip ? `Aktif${u.vip_until ? " s/d " + fmtDate(u.vip_until) : ""}` : "Tidak";
  return (
    <div className="space-y-4" data-testid="txn-detail-content">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="font-heading font-black text-lg">Detail Transaksi</h3>
          <p className="text-xs text-su-muted font-mono">{t.order_id}</p>
        </div>
        <button onClick={onClose} data-testid="txn-detail-close" className="text-su-muted hover:text-su-text text-xl leading-none">✕</button>
      </div>

      <div className="rounded-2xl bg-su-bg/60 border-2 border-su-border p-4 space-y-1 text-sm">
        <div className="flex justify-between"><span className="text-su-muted">Siswa</span><span className="font-semibold">{u.name} <span className="text-su-muted">({u.email})</span></span></div>
        <div className="flex justify-between"><span className="text-su-muted">Produk</span><span className="font-semibold">{t.product_name}</span></div>
        <div className="flex justify-between"><span className="text-su-muted">Nominal</span><span className="font-heading font-black">{fmt(t.amount, t.currency)}</span></div>
        <div className="flex justify-between"><span className="text-su-muted">Status bayar</span><span className="font-heading font-black">{(STATUS_BADGE[t.status] || STATUS_BADGE.pending).label}</span></div>
        <div className="flex justify-between"><span className="text-su-muted">Masuk ke akun</span>{t.granted ? <span className="text-su-success font-heading font-black">✓ Sudah{t.manual_grant ? " (manual)" : ""}</span> : <span className="text-su-danger font-heading font-black">✗ Belum</span>}</div>
      </div>

      <div>
        <p className="font-heading font-black text-sm mb-1">Saldo Siswa {pending ? "(sebelum → perkiraan sesudah)" : "(saat ini)"}</p>
        <div className="rounded-2xl bg-su-surface border-2 border-su-border px-4">
          <Row label="Token" before={u.tokens ?? 0} after={pending && g.tokens ? (u.tokens ?? 0) + g.tokens : null} changed={pending && !!g.tokens} />
          <Row label="Energi" before={u.energy ?? 0} after={pending && g.energy ? "penuh (100)" : null} changed={pending && !!g.energy} />
          <Row label="VIP" before={fmtVip} after={pending && g.vip_days ? vipTxt(g.vip_days) : null} changed={pending && !!g.vip_days} />
          {(slots > 0 || d.current_pack_slots != null) && (
            <Row label="Slot Paket Tryout" before={d.current_pack_slots ?? 0} after={pending && slots ? (d.current_pack_slots ?? 0) + slots : null} changed={pending && !!slots} />
          )}
        </div>
        {t.granted && (
          <p className="text-xs text-su-muted mt-2">Transaksi ini menambahkan: {[g.tokens ? `${g.tokens} token` : null, g.energy ? "energi penuh" : null, vipTxt(g.vip_days), slots ? `${slots} slot tryout` : null].filter(Boolean).join(", ") || "—"}.</p>
        )}
      </div>

      {pending && (
        <button onClick={onCredit} disabled={crediting} data-testid="txn-detail-credit"
          className="w-full py-3 rounded-2xl bg-su-danger text-white font-heading font-black hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2">
          {crediting ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />} Kreditkan Manual ke Akun Siswa
        </button>
      )}
    </div>
  );
}
